import { useMemo, type JSX, type ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
  type PressableStateCallbackType,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { type Colors, useColors } from "../theme";

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "md" | "sm";

export interface ButtonProps {
  /** Variant. "primary" = coral fill, "secondary" = bordered/brand, "ghost" = text-only/muted. */
  variant?: ButtonVariant;
  /** Size. "md" (default) or compact "sm". */
  size?: ButtonSize;
  /** Tap handler. */
  onPress?: () => void;
  /** Dim + non-interactive. */
  disabled?: boolean;
  /** Show a spinner, disable interaction, and hold the label's width stable. */
  busy?: boolean;
  /** Stretch to fill the parent's cross axis (width 100%). */
  fullWidth?: boolean;
  /** Button text. Provide this OR `children`. */
  label?: string;
  /** Custom content. Provide this OR `label`. */
  children?: ReactNode;
  /** Override the announced label (defaults to `label`). */
  accessibilityLabel?: string;
  /** Forwarded to the underlying Pressable (for tests). */
  testID?: string;
  /** Extra style on the outer pressable. */
  style?: StyleProp<ViewStyle>;
}

/**
 * Shared coral-CTA button for the PassFirst Expo client.
 *
 * Replaces the hand-rolled `Pressable` + `Text` CTA every screen rolled
 * (paddingVertical:16, radius.lg, coral fill, bold inverse text). Pulls all
 * color from `useColors()` so it tracks light/dark, and all spacing/type from
 * `@passfirst/ui` tokens.
 *
 * - `variant`: "primary" (coral fill) | "secondary" (bordered, brand text) |
 *   "ghost" (text-only, muted). Defaults to "primary".
 * - `size`: "md" (default) | "sm".
 * - `busy` shows an `ActivityIndicator`, disables the button, and keeps the
 *   label rendered (transparent) so the width never jumps.
 * - Pressed state dims to ~0.85 via Pressable's function-style `style`,
 *   suppressed while disabled/busy.
 */
export function Button({
  variant = "primary",
  size = "md",
  onPress,
  disabled = false,
  busy = false,
  fullWidth = false,
  label,
  children,
  accessibilityLabel,
  testID,
  style,
}: ButtonProps): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const isInteractive = !disabled && !busy;
  const content = children ?? label;
  const labelStyle = [
    styles.label,
    styles[`${variant}Label`],
    size === "sm" && styles.labelSm,
  ];
  const spinnerColor =
    variant === "primary" ? colors.actionText : colors.brand;

  const containerStyle = ({ pressed }: PressableStateCallbackType) => [
    styles.base,
    styles[variant],
    size === "sm" && styles.sizeSm,
    fullWidth && styles.fullWidth,
    disabled && styles.disabled,
    pressed && isInteractive && styles.pressed,
    style,
  ];

  return (
    <Pressable
      onPress={isInteractive ? onPress : undefined}
      disabled={!isInteractive}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled, busy }}
      testID={testID}
      style={containerStyle}
    >
      <View style={styles.inner}>
        {busy ? (
          <ActivityIndicator
            size="small"
            color={spinnerColor}
            style={styles.spinner}
          />
        ) : null}
        {typeof content === "string" ? (
          <Text style={[labelStyle, busy && styles.labelHidden]}>
            {content}
          </Text>
        ) : (
          <View style={busy ? styles.labelHidden : undefined}>{content}</View>
        )}
      </View>
    </Pressable>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    base: {
      paddingVertical: space[4],
      paddingHorizontal: space[5],
      borderRadius: radius.lg,
      alignItems: "center",
      justifyContent: "center",
      alignSelf: "flex-start",
      borderWidth: 1,
      borderColor: "transparent",
    },
    sizeSm: {
      paddingVertical: space[2],
      paddingHorizontal: space[4],
    },
    fullWidth: {
      alignSelf: "stretch",
      width: "100%",
    },
    primary: {
      backgroundColor: colors.action,
      borderColor: colors.action,
    },
    secondary: {
      backgroundColor: "transparent",
      borderColor: colors.borderBrand,
    },
    ghost: {
      backgroundColor: "transparent",
      borderColor: "transparent",
    },
    disabled: {
      opacity: 0.45,
    },
    pressed: {
      opacity: 0.85,
    },
    inner: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
    },
    spinner: {
      position: "absolute",
    },
    label: {
      ...textStyle.buttonPrimary,
      textAlign: "center",
    },
    labelSm: {
      ...textStyle.buttonSecondary,
    },
    labelHidden: {
      opacity: 0,
    },
    primaryLabel: {
      // Match the established coral CTA: bold, body size.
      fontFamily: fontFamily.bold,
      color: colors.actionText,
    },
    secondaryLabel: {
      color: colors.textBrand,
    },
    ghostLabel: {
      color: colors.textMuted,
    },
  });
}
